import React from 'react';
import { useLocation } from 'react-router';

import { URLModal } from 'src/components/common/URLModal';
import { useQueryParam } from 'src/hooks/useQueryParam';
import Config from 'src/lib/config';
import { AdditionalModalState } from 'src/lib/routing';

import { SavedOperationAction } from '../../hooks/useExplorerState/useEditorTabState/useSavedOperationLocalState';

import { ConfigureCollectionModal } from './configureCollectionModal/ConfigureCollectionModal';
import { CreateCollectionModal } from './createCollectionModal/CreateCollectionModal';
import { DeleteCollectionEntryModal } from './deleteCollectionEntryModal/DeleteCollectionEntryModal';
import { DeleteCollectionModal } from './deleteCollectionModal/DeleteCollectionModal';
import { DuplicateCollectionModal } from './duplicateCollectionModal/DuplicateCollectionModal';
import { RenameCollectionEntryModal } from './renameCollectionEntryModal/RenameCollectionEntryModal';
import { SaveCollectionEntryFromExplorerModal } from './saveCollectionEntryFromExplorerModal/SaveCollectionEntryFromExplorerModal';
import { ViewCollectionDetailsModal } from './viewCollectionDetailsModal/ViewCollectionDetailsModal';
import { ViewCollectionEntryDetailsModal } from './viewCollectionEntryDetailsModal/ViewCollectionEntryDetailsModal';

export const OperationCollectionsModals = ({
  dispatchSavedOperationLocalStateAction,
}: {
  dispatchSavedOperationLocalStateAction: (
    action: SavedOperationAction,
  ) => void;
}) => {
  const { state } = useLocation<
    AdditionalModalState[keyof AdditionalModalState] | undefined
  >();
  const collectionId = useQueryParam('collectionId');

  const graphRef = React.useMemo(
    () => (state && 'graphRef' in state ? state.graphRef : undefined),
    [state],
  );

  return (
    <>
      <URLModal name={Config.modals.createPersonalOperationCollection}>
        {({ isOpen, closeModal }) => {
          if (graphRef === undefined) {
            closeModal();
            return null;
          }
          return (
            <CreateCollectionModal
              isOpen={isOpen}
              graphRef={graphRef}
              isShared={false}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.createSharedOperationCollection}>
        {({ isOpen, closeModal }) => {
          if (graphRef === undefined || graphRef === null) {
            closeModal();
            return null;
          }
          return (
            <CreateCollectionModal
              isOpen={isOpen}
              graphRef={graphRef}
              isShared={true}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.deleteOperationCollection}>
        {({ isOpen, closeModal }) => {
          // Missing state
          if (!state || !('collectionId' in state)) {
            closeModal();
            return null;
          }
          return (
            <DeleteCollectionModal
              isOpen={isOpen}
              collectionId={state.collectionId}
              dispatchSavedOperationLocalStateAction={
                dispatchSavedOperationLocalStateAction
              }
              onClose={closeModal}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.duplicateOperationCollection}>
        {({ isOpen, closeModal }) => {
          // Missing state
          if (!state || !('collectionId' in state)) {
            closeModal();
            return null;
          }
          return (
            <DuplicateCollectionModal
              isOpen={isOpen}
              collectionId={state.collectionId}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.viewCollectionEntryDetails}>
        {({ isOpen, closeModal }) => {
          if (!state || !('collectionEntryId' in state)) {
            closeModal();
            return null;
          }
          return (
            <ViewCollectionEntryDetailsModal
              isOpen={isOpen}
              collectionId={state.collectionId}
              collectionEntryId={state.collectionEntryId}
              onClose={closeModal}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.deleteOperationCollectionEntry}>
        {({ isOpen, closeModal }) => {
          if (!state || !('collectionEntryId' in state)) {
            closeModal();
            return null;
          }
          return (
            <DeleteCollectionEntryModal
              isOpen={isOpen}
              collectionId={state.collectionId}
              collectionEntryId={state.collectionEntryId}
              dispatchSavedOperationLocalStateAction={
                dispatchSavedOperationLocalStateAction
              }
              onClose={closeModal}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.renameOperationCollectionEntry}>
        {({ isOpen, closeModal }) => {
          // Missing state
          if (!state || !('collectionEntryId' in state)) {
            closeModal();
            return null;
          }
          return (
            <RenameCollectionEntryModal
              isOpen={isOpen}
              collectionId={state.collectionId}
              collectionEntryId={state.collectionEntryId}
              onClose={closeModal}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.saveOperationCollectionEntry}>
        {({ isOpen, closeModal }) => {
          // Missing state
          if (
            !state ||
            !('operationTabId' in state) ||
            !('collectionEntryState' in state) ||
            graphRef === undefined
          ) {
            closeModal();
            return null;
          }
          return (
            <SaveCollectionEntryFromExplorerModal
              isOpen={isOpen}
              graphRef={graphRef}
              operationTabId={state.operationTabId}
              operationState={state.collectionEntryState}
              dispatchSavedOperationLocalStateAction={
                dispatchSavedOperationLocalStateAction
              }
              onClose={closeModal}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.configureOperationCollection}>
        {({ isOpen, closeModal }) => {
          // Missing query params
          if (!collectionId) {
            closeModal();
            return null;
          }
          return (
            <ConfigureCollectionModal
              isOpen={isOpen}
              collectionId={collectionId}
              onClose={() => closeModal(undefined, ['collectionId'])}
            />
          );
        }}
      </URLModal>
      <URLModal name={Config.modals.viewCollectionDetails}>
        {({ isOpen, closeModal }) => {
          // Missing query params
          if (!collectionId) {
            closeModal();
            return null;
          }
          return (
            <ViewCollectionDetailsModal
              isOpen={isOpen}
              collectionId={collectionId}
              onClose={() => closeModal(undefined, ['collectionId'])}
            />
          );
        }}
      </URLModal>
    </>
  );
};
